import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { RbacFormField, RbacFormFieldOption } from '../../../../shared/component/rbac-form/rbac-form-field.model';
import { RbacModalComponent } from '../../../../shared/component/rbac-modal/rbac-modal.component';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { RbacTableColumn } from '../../../../shared/component/rbac-table/rbac-table-column.model';
import { Module, Role, HIERARCHICAL_ROUTES } from '../rbac.models';
import { RbacTableComponent } from '../../../../shared/component/rbac-table/rbac-table.component';
import { RbacFormComponent } from '../../../../shared/component/rbac-form/rbac-form.component';
import { RoleService } from '../../../../shared/services/rbackservices/role.service';
import { ToastService } from '../../../../shared/services/toast.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-module-management',
  standalone: true,
  imports: [CommonModule, RbacTableComponent, RbacFormComponent, RbacModalComponent],
  templateUrl: './module-management.component.html',
  styleUrls: ['./module-management.component.scss']
})
export class ModuleManagementComponent implements OnInit {
  @Output() manageSubModules = new EventEmitter<Module>();

  modules: Module[] = [];
  loading = false;
  saving = false;
  modalOpen = false;
  editingModule: Module | null = null;

  columns: RbacTableColumn[] = [
    { key: 'name', label: 'Module Name' },
    { key: 'url', label: 'URL' },
    { key: 'category', label: 'Category' },
    { key: 'order', label: 'Order', width: '80px' },
    { key: 'is_view', label: 'Read', type: 'badge-yn' },
    { key: 'is_add', label: 'Add', type: 'badge-yn' },
    { key: 'is_edit', label: 'Edit', type: 'badge-yn' },
    { key: 'sub_module', label: 'Has Sub-Modules', type: 'badge-yn' },
    { key: 'tab', label: 'Shown as Tab', type: 'badge-yn' },
    { key: 'status', label: 'Status', type: 'status' }
  ];

  fields: RbacFormField[] = [
    { key: 'name', label: 'Module Name', type: 'text', required: true, placeholder: 'e.g. Site Summary' },
    {
      key: 'url',
      label: 'URL',
      type: 'select',
      placeholder: 'Select URL...',
      options: HIERARCHICAL_ROUTES.map(r => ({ label: r.label, value: r.path }))
    },
    { key: 'order', label: 'Order', type: 'number', required: true },
    { key: 'icon', label: 'Icon', type: 'icon-select', placeholder: 'Select an icon...', options: [] },
    { key: 'sub_module', label: 'Has Sub-Modules?', type: 'toggle' },
    { key: 'is_view', label: 'Can View?', type: 'toggle' },
    { key: 'is_add', label: 'Can Add?', type: 'toggle' },
    { key: 'is_edit', label: 'Can Edit?', type: 'toggle' },
    { key: 'tab', label: 'Shown as Tab?', type: 'toggle' },
    { key: 'category', label: 'Category', type: 'select', placeholder: 'Select category types...', options: [] }
  ];

  roles: Role[] = [];

  constructor(
    private configurationService: ApiCallsService,
    private roleService: RoleService,
    private toast: ToastService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadModules();
    this.loadCategories();
    this.loadIcons();
  }

  private loadCategories(): void {
    this.configurationService.getCategories().subscribe({
      next: (res) => {
        const categories = res?.Data ?? res ?? [];
        const categoryField = this.fields.find(f => f.key === 'category');
        if (categoryField) {
          categoryField.options = categories.map((c: any) => ({
            label: c.name,
            value: c.category_type
          }));
          this.fields = [...this.fields];
        }
      }
    });
  }

  // Fetches the sprite as raw text and parses out each <symbol id="...">.
  // value = symbol id (what gets saved), label = humanized id (what the
  // dropdown shows next to the icon preview).
  private loadIcons(): void {
    const spritePath = 'assets/svg/icon-sprite.svg';
    this.http.get(spritePath, { responseType: 'text' }).subscribe({
      next: (svgText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const symbols = Array.from(doc.querySelectorAll('symbol'));

        const options: RbacFormFieldOption[] = symbols
          .map((sym) => sym.getAttribute('id'))
          .filter((id): id is string => !!id)
          .map((id) => ({ value: id, label: this.humanizeIconId(id) }));

        const iconField = this.fields.find(f => f.key === 'icon');
        if (iconField) {
          iconField.options = options;
          iconField.spritePath = spritePath;
          this.fields = [...this.fields];
        }
      },
      error: () => {
        // Sprite failed to load — icon field just shows an empty list.
      }
    });
  }

  private humanizeIconId(id: string): string {
    return id
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  loadModules(): void {
    this.loading = true;
    this.configurationService
      .get({ type: 'M' })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          const all = res?.Data ?? res ?? [];
          this.modules = all.filter((row: any) => row.type === 'M');
        },
        error: () => (this.modules = [])
      });
  }

  openAdd(): void {
    this.editingModule = null;
    this.modalOpen = true;
  }

  openEdit(module: Module): void {
    this.editingModule = module;
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.editingModule = null;
  }

  onSave(value: Record<string, any>): void {
    this.saving = true;
    const { _id, ...rest } = value;
    const payload: any = { ...rest, order: Number(value['order']), type: 'M', parent_id: '' };
    const action = _id ? 'Updated' : 'Added';

    const request$ = _id
      ? this.configurationService.update({ id: _id, ...payload })
      : this.configurationService.add(payload as Module);

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
          const errorMsg = res?.Message || res?.message || `Failed to ${action.toLowerCase()} Module`;
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          return;
        }
        this.toast.showToast(`Module ${action} Successfully`, 'colored', 5000, 'success');
        this.closeModal();
        this.loadModules();
      },
      error: err => {
        const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || `Failed to ${action.toLowerCase()} Module`;
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
      }
    });
  }

  onToggleStatus(module: Module): void {
    if (!module._id) { return; }
    const nextStatus = module.status === 'N' ? 'Y' : 'N';
    this.configurationService
      .update({ id: module._id, status: nextStatus, order: module.order, type: 'M', parent_id: '' })
      .subscribe({
        next: (res: any) => {
          if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
            const errorMsg = res?.Message || res?.message || 'Failed to update status';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            return;
          }
          this.loadModules();
        },
        error: err => {
          const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to update status';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        }
      });
  }

  onDelete(module: Module): void {
    const id = module._id || module.id;
    if (!id) { return; }
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you really want to delete module "${module.name}"? Its sub-modules should be removed first.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    }).then((result) => {
      if (!result.isConfirmed) {
        this.toast.showToast('Action cancelled', 'colored', 3000, 'info');
        return;
      }
      this.configurationService
        .update({
          id,
          ...module,
          status: 'N',
          order: Number(module.order),
          type: 'M',
          parent_id: ''
        })
        .subscribe({
          next: (res: any) => {
            if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
              const errorMsg = res?.Message || res?.message || 'Failed to delete module';
              this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
              return;
            }
            this.toast.showToast('Module deleted successfully', 'colored', 5000, 'success');
            this.loadModules();
          },
          error: err => {
            const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to delete module';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          }
        });
    });
  }
}